import { Form, message, Spin } from 'antd';
import { Banner } from 'components/ui/Banner';
import { useCallback, useEffect, useState } from 'react';
import { familyService } from 'services';
import { FAMILY_APPLICATION_STEPS } from '../../familyApplicationConstants';
import PageMeta from 'components/PageMeta/PageMeta';
import AddressFields from 'components/AddressFields';
import { getNextStep, getPreviousStep, stepConfigAtom } from '../../atoms';
import { useAtomValue } from 'jotai';
import { getApplicantFirstName } from '../../utils';
import { ApplicationWizardActions } from 'components/application-wizards/ApplicationWizardActions';

export function FamilyApplicationLocation({
  application,
  setApplication,
  household,
  loading,
  setStep,
  readOnly = false,
}) {
  const stepConfig = useAtomValue(stepConfigAtom);

  const [submitting, setSubmitting] = useState(false);

  // Form state
  const [form] = Form.useForm();

  const onSubmit = useCallback(
    async (values) => {
      const _goToNextStep = (_newApplication) => {
        // Use getNextStep to determine the next step
        const nextStep = getNextStep(FAMILY_APPLICATION_STEPS.LOCATION_INFORMATION, _newApplication, stepConfig);
        setStep({
          applicationId: _newApplication?.id,
          newStep: nextStep,
        });
      };

      if (readOnly) {
        _goToNextStep(application);
        return;
      }

      try {
        setSubmitting(true);

        const _newSponsorData = await familyService.addUpdateAdult(household?.id, application?.applicant?.id ?? null, {
          ...application?.applicant,
          homeAddress: values?.homeAddress,
        });

        // Check if address actually changed
        const currentAddress = application?.applicant?.homeAddress;
        const newAddress = values?.homeAddress;
        const addressChanged = JSON.stringify(currentAddress || null) !== JSON.stringify(newAddress || null);

        let _newApplication = await familyService.saveApplication({
          ...application,
          applicant: {
            ...application?.applicant,
            ..._newSponsorData,
          },
        });

        console.log('Address change detection:', {
          currentAddress,
          newAddress,
          addressChanged,
          childrenCount: _newApplication?.children?.length || 0,
        });

        // FAMILY-APPS__DATA_CLEANING - Clear dependent fields when applicant address changes
        if (addressChanged) {
          // Clear spouse's livesWithApplicant
          if (_newApplication?.additionalParents?.[0]) {
            const updatedSpouse = await familyService.addUpdateAdult(
              household?.id,
              _newApplication.additionalParents[0].id,
              {
                ..._newApplication.additionalParents[0],
                addressSameAsApplicant: null,
              },
            );
            _newApplication = await familyService.saveApplication({
              ..._newApplication,
              additionalParents: [updatedSpouse],
            });
          }

          // Clear children's living situation
          if (_newApplication?.children?.length > 0) {
            const updatedChildren = _newApplication.children.map((child) => ({
              ...child,
              livingSituation: null,
            }));

            const childUpdatePromises = updatedChildren.map((child) =>
              familyService.addUpdateFamilyChild(household?.id, child.id, child),
            );
            const savedChildren = await Promise.all(childUpdatePromises);

            _newApplication = await familyService.saveApplication({
              ..._newApplication,
              children: savedChildren,
            });
          }
        }

        setApplication(_newApplication);
        _goToNextStep(_newApplication);
      } catch (error) {
        message.error('Something went wrong');
        newrelic.noticeError(error);
      } finally {
        setSubmitting(false);
      }
    },
    [application, household?.id, readOnly, setApplication, setStep, stepConfig],
  );

  // Set initial form values
  useEffect(() => {
    if (application?.applicant) {
      form.setFieldsValue({
        homeAddress: application?.applicant?.homeAddress,
      });
    }
  }, [application, form]);

  if (loading) {
    return (
      <div className="p-8">
        <Spin className="table m-auto" />
      </div>
    );
  }

  return (
    <Spin spinning={false}>
      <PageMeta title="Location Information" />

      <Form form={form} layout="vertical" onFinish={onSubmit} disabled={readOnly}>
        <div className="p-7 pt-0">
          <Banner
            variant="inline"
            question={`What is ${getApplicantFirstName(application?.applicant)}'s current home address?`}
            guidance="If overseas or in Permanent Change of Station (PCS), enter next US address or home of record"
            className="my-12"
            data-testid="address-question-banner"
          />

          <AddressFields addressKey="homeAddress" data-testid="home-address-fields" />
        </div>

        <ApplicationWizardActions
          applicationIsReadOnly={readOnly}
          backButtonProps={{
            disabled: submitting,
            'data-testid': 'location-back-button',
            onClick: () => {
              // Use getPreviousStep to determine the previous step
              const previousStep = getPreviousStep(
                FAMILY_APPLICATION_STEPS.LOCATION_INFORMATION,
                application,
                stepConfig,
              );

              setStep({
                applicationId: application?.id,
                newStep: previousStep,
              });
            },
          }}
          nextButtonProps={{
            loading: submitting,
            'data-testid': 'location-next-button',
          }}
        />
      </Form>
    </Spin>
  );
}
